import { useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import BuyOrderForm from "./BuyOrderForm.js";
import { catalogFetch } from "./catalogApi.js";
import OrdersTable from "./OrdersTable.js";
import MarketOrdersTable from "./MarketOrdersTable.js";
import OpenOrdersTable from "./OpenOrdersTable.js";

const DEFAULT_YES_PERCENT = 50;

function MarketWorkspaceTabs({
  markets,
  selectedGroup = null,
  selectedMarketId,
  onSelectMarket,
  orders,
  authToken = "",
  activeTab,
  disabled = false,
  loading = false,
  onSelectTab,
  onPlaceOrder,
  onCancelOrder,
  onTakeOppositeOrder,
}) {
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [modalOutcome, setModalOutcome] = useState("");
  const [quotesByMarket, setQuotesByMarket] = useState({});
  const selectedMarketIds = markets.map((market) => market.market_id).filter(Boolean);
  const userOrdersForSelectedCategory = orders.filter(
    (order) => selectedMarketIds.includes(order.market_id)
  );
  const hasSelectedMarkets = selectedMarketIds.length > 0;
  const isCategoricalGroup = String(selectedGroup?.category || "").toLowerCase() === "categorical";
  const selectedMarket = useMemo(
    () => markets.find((market) => market.market_id === selectedMarketId) || null,
    [markets, selectedMarketId]
  );

  const getYesPercent = (market) => {
    const yesQuote = quotesByMarket[market?.market_id]?.YES;
    const markPrice = Number(yesQuote?.mark_price);
    if (Number.isFinite(markPrice) && markPrice >= 0 && markPrice <= 1) {
      return Math.round(markPrice * 100);
    }

    const displayPrice = Number(yesQuote?.display_price);
    if (Number.isFinite(displayPrice) && displayPrice >= 0 && displayPrice <= 1) {
      return Math.round(displayPrice * 100);
    }

    const directYesPrice = Number(market?.yes_price);
    if (Number.isFinite(directYesPrice)) {
      const normalized = directYesPrice > 1 ? directYesPrice / 100 : directYesPrice;
      if (normalized >= 0 && normalized <= 1) {
        return Math.round(normalized * 100);
      }
    }

    const priceRows = Array.isArray(market?.prices) ? market.prices : [];
    const yesRow = priceRows.find(
      (row) => String(row?.outcome || "").toUpperCase() === "YES"
    );
    const binaryRow = priceRows.find(
      (row) => String(row?.outcome || "").toUpperCase() === "BINARY"
    );
    const fallbackPrice = Number(yesRow?.price ?? binaryRow?.price);
    if (Number.isFinite(fallbackPrice)) {
      const normalized = fallbackPrice > 1 ? fallbackPrice / 100 : fallbackPrice;
      if (normalized >= 0 && normalized <= 1) {
        return Math.round(normalized * 100);
      }
    }

    return DEFAULT_YES_PERCENT;
  };

  useEffect(() => {
    if (!selectedMarketId) {
      setShowTradeModal(false);
    }
  }, [selectedMarketId]);

  useEffect(() => {
    let cancelled = false;

    const loadQuotes = async () => {
      const targetMarketIds = markets.map((market) => market.market_id).filter(Boolean);
      if (targetMarketIds.length === 0 || !authToken) {
        setQuotesByMarket({});
        return;
      }

      try {
        const responses = await Promise.allSettled(
          targetMarketIds.map(async (marketId) => {
            const body = await catalogFetch(
              `/markets/${encodeURIComponent(marketId)}/quote?outcome=YES`,
              authToken
            );
            return {
              marketId,
              quote: body,
            };
          })
        );

        if (!cancelled) {
          const nextQuotesByMarket = {};
          responses.forEach((result) => {
            if (result.status !== "fulfilled") {
              return;
            }
            const entry = result.value;
            nextQuotesByMarket[entry.marketId] = { YES: entry.quote };
          });
          setQuotesByMarket(nextQuotesByMarket);
        }
      } catch (_err) {
        if (!cancelled) {
          setQuotesByMarket({});
        }
      }
    };

    loadQuotes();
    return () => {
      cancelled = true;
    };
  }, [authToken, markets]);

  const openTradeModal = (marketId, outcome = "") => {
    if (marketId && marketId !== selectedMarketId) {
      onSelectMarket?.(marketId);
    }
    setModalOutcome(outcome);
    setShowTradeModal(true);
  };

  return (
    <Row className="g-4">
      <Col xs={12}>
        <div className="market-group-hero">
          <div className="market-group-copy">
            <h2>{selectedGroup?.group_title || "Market Group"}</h2>
          </div>
        </div>
      </Col>
      {hasSelectedMarkets && (
        <Col xs={12}>
          <div className="market-option-card">
            <div className="market-option-rail">
            {markets.map((market) => {
              const isSelected = market.market_id === selectedMarketId;
              const yesPercent = getYesPercent(market);
              return (
                <button
                  key={market.market_id}
                  type="button"
                  className={`market-option-chip${isSelected ? " is-selected" : ""}`}
                  onClick={() => {
                    onSelectMarket?.(market.market_id);
                    openTradeModal(market.market_id);
                  }}
                >
                  <span className="market-option-chip-copy">
                    <span className="market-option-chip-text">
                      <span className="market-option-chip-label">
                        {market.option_label || market.title || market.market_id}
                      </span>
                      <span className={`market-option-chip-accent${isSelected ? " is-selected" : ""}`} />
                    </span>
                    {Number.isInteger(market.group_order) && (
                      <span className="market-option-chip-order">#{market.group_order + 1}</span>
                    )}
                  </span>
                  <span className="market-option-chip-probability">YES {yesPercent}%</span>
                </button>
              );
            })}
            </div>
            <div className="market-option-card-footer">
              <span>{isCategoricalGroup ? "Categorical market" : "Market group"}</span>
              <span>{markets.length} {markets.length === 1 ? "market" : "markets"}</span>
            </div>
          </div>
        </Col>
      )}
      <Col xs={12}>
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => onSelectTab(key || "open-orders")}
          className="mb-3"
        >
          <Tab eventKey="open-orders" title="Open Orders">
            <div className="pt-3">
              <OpenOrdersTable
                disabled={disabled}
                authToken={authToken}
                selectedMarketId={selectedMarketId}
                selectedMarketIds={selectedMarketIds}
                availableMarkets={markets}
                onTakeOpposite={onTakeOppositeOrder}
              />
            </div>
          </Tab>
          <Tab eventKey="market-orders" title="Orders">
            <div className="pt-3">
              <MarketOrdersTable
                disabled={disabled}
                authToken={authToken}
                onTakeOpposite={onTakeOppositeOrder}
                selectedMarketId={selectedMarketId}
                selectedMarketIds={selectedMarketIds}
                availableMarkets={markets}
                showMarketSelector={false}
              />
            </div>
          </Tab>
          <Tab eventKey="my-market-orders" title="My Orders">
            <div className="pt-3">
              <OrdersTable
                orders={userOrdersForSelectedCategory}
                authToken={authToken}
                onCancelOrder={onCancelOrder}
                disabled={disabled}
                emptyMessage="You have no orders for this category"
              />
            </div>
          </Tab>
        </Tabs>
      </Col>
      <Modal
        show={showTradeModal}
        onHide={() => setShowTradeModal(false)}
        centered
        size="lg"
        className="trade-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedGroup?.group_title || "Trade Market"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="trade-modal-copy">
            <div className="trade-modal-kicker">Selected outcome</div>
            <h3>
              {selectedMarket?.option_label || selectedMarket?.title || selectedMarket?.market_id || "Market"}
            </h3>
            <p>Review the market, choose a side, and submit the order from one focused ticket.</p>
          </div>
          <BuyOrderForm
            onSubmit={onPlaceOrder}
            onSubmitSuccess={() => setShowTradeModal(false)}
            disabled={disabled || !hasSelectedMarkets}
            authToken={authToken}
            selectedMarketId={selectedMarketId}
            availableMarkets={markets}
            showMarketSelector={false}
            renderMode="tabs"
            initialOutcome={modalOutcome}
          />
        </Modal.Body>
      </Modal>
    </Row>
  );
}

export default MarketWorkspaceTabs;
